import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { messages } from './messages';
import style from './style.module.css';
import { useLuxonUserDateFormat, useUtcFormattedDate } from '../../../../../../../hooks/use-datetime-formats';
import { getDateAsUtcNoonInISO } from '../../../../../../../services/datetime-service';
import { editLotForProduct } from '../../../../../../../store/inventory/inventory.api';
import { ExpirationDateInput } from '../../shared-components/expiration-date-input';
import { LotInput } from '../../shared-components/lot-input';

/**
 * @param {object} props
 * @param {() => void} props.onReloadLocations
 * @param {string} props.productId
 * @param {string} [props.lot]
 * @param {string} props.lotUuid
 * @param {string} [props.expirationDate]
 * @param {() => void} props.onOuterAction
 * @param {(boolean) => void} props.setIsOpen
 * @returns {JSX.Element}
 */

export const EditLotPopoverBody = ({ onReloadLocations, productId, lot = '', lotUuid, expirationDate = '', onOuterAction, setIsOpen }) => {
  const initialExpirationDate = useUtcFormattedDate(expirationDate);
  const [newLot, setNewLot] = useState(lot);
  const [newExpirationDate, setNewExpirationDate] = useState(initialExpirationDate);
  const luxonUserDateFormat = useLuxonUserDateFormat();

  const onEditLot = useCallback(() => {
    const expirationDateISO = getDateAsUtcNoonInISO(newExpirationDate, luxonUserDateFormat);
    const editLotProps = {
      productId,
      lot: newLot,
      expirationDate: expirationDateISO,
    };
    const editLotPromise = editLotForProduct(lotUuid, editLotProps);
    editLotPromise.promise
      .then(() => {
        onReloadLocations();
        setIsOpen(false);
      })
      .catch(() => {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.editLotErrorTitle} />,
          content: <FormattedMessage {...messages.editLotErrorMessage} />,
          duration: NOTIFICATION_DURATIONS.SHORT,
        });
        onOuterAction();
      });
    return () => editLotPromise?.cancel();
  }, [productId, onReloadLocations, newExpirationDate, newLot, lotUuid, setIsOpen, onOuterAction, luxonUserDateFormat]);
  const isEditDisabled = !newLot || (newLot === lot && newExpirationDate === initialExpirationDate);

  return (
    <div data-ignore-root-focus>
      <div className={style['popover-title']}>
        <FormattedMessage {...messages.editLotPopOverTitle} />
      </div>
      <div className={style['popover-body']}>
        <div className={style['body-inputs']}>
          <LotInput lot={newLot} setLot={setNewLot} />
          <ExpirationDateInput expirationDate={newExpirationDate} setExpirationDate={setNewExpirationDate} />
        </div>
      </div>
      <div className={style['popover-buttons']}>
        <Button onClick={onOuterAction} theme='tertiary'>
          <FormattedMessage {...messages.editLotPopoverCancel} />
        </Button>
        <Button disabled={isEditDisabled} onClick={onEditLot} theme='primary'>
          <FormattedMessage {...messages.editLotPopoverEdit} />
        </Button>
      </div>
    </div>
  );
};
